import data from "../../../data/finanzaemter.json";

/**
 * Laufzeit-Lookups über die generierte Finanzämter-Liste
 * (apps/web/src/data/finanzaemter.json, erzeugt aus dem GEMFA-Export).
 *
 * Zum Thema „doppelte Einträge": Es gibt keine. Was in der Auswahl wie eine
 * Dublette aussieht — fünfmal „München Gewinneinkünfte", fünfmal „Hamburg für
 * Verkehrst. und Grundbesitz" — sind real existierende, voneinander unabhängige
 * Finanzämter. Großstadt-Ämter werden nach Zuständigkeitsbezirken nummeriert,
 * nicht benannt; ihr einziges Unterscheidungsmerkmal ist die vierstellige
 * Bundesfinanzamtsnummer (BuFa). Wer hier dedupliziert, löscht echte Ämter.
 *
 * Gespeichert wird am Kontakt weiterhin ein Name als Freitext. Damit die
 * Auswahl nicht ins Leere läuft — vorher speicherten alle fünf Münchner
 * denselben Text, die Wahl zwischen ihnen verfiel also beim Speichern —
 * trägt der Name bei Mehrdeutigkeit die BuFa mit: „München Gewinneinkünfte
 * (9144)". Die 571 eindeutigen Ämter bleiben unverändert.
 */

export interface Finanzamt {
  bufa: string;
  name: string;
  ort: string;
  plz: string;
}

const byBufa = (data as { byBufa: Record<string, { name: string; ort: string; plz: string }> }).byBufa;
const list = (data as { list: Finanzamt[] }).list;

const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/**
 * Namen, die mehr als ein Amt tragen — für sie allein wird die BuFa Teil der
 * Bezeichnung. Schlüssel ist der Name samt Ort und PLZ: „Hamburg-Nord" gibt es
 * zweimal mit verschiedenen PLZ, dort reicht der Ortszusatz nicht.
 */
const mehrdeutig: Set<string> = (() => {
  const zaehler = new Map<string, number>();
  for (const f of list) zaehler.set(f.name, (zaehler.get(f.name) ?? 0) + 1);
  return new Set([...zaehler].filter(([, n]) => n > 1).map(([name]) => name));
})();

/**
 * Die Bezeichnung, unter der ein Amt gespeichert und angezeigt wird.
 * Eindeutig — das ist der Zweck.
 */
export function finanzamtBezeichnung(fa: Finanzamt): string {
  return mehrdeutig.has(fa.name) ? `${fa.name} (${fa.bufa})` : fa.name;
}

/**
 * Rückweg: gespeicherte Bezeichnung → Amtsnummer.
 *
 * Trägt die Bezeichnung die Nummer bereits („München Gewinneinkünfte (9144)"),
 * wird sie herausgelesen. Sonst wird über den Namen gesucht — was genau dann
 * gelingt, wenn er eindeutig ist, also für 571 der 593 Ämter. Damit füllt sich
 * die Nummer auch bei Kontakten, die den Namen aus der Zeit vor der Umstellung
 * tragen, beim nächsten Speichern von selbst; ein Backfill in der Datenbank ist
 * dafür nicht nötig.
 *
 * Bei einem mehrdeutigen Altwert („München Gewinneinkünfte" ohne Nummer) bleibt
 * das Ergebnis bewusst leer: Ein geratenes Amt wäre schlimmer als keins.
 */
export function bufaAusBezeichnung(bezeichnung: string | null | undefined): string | undefined {
  const text = String(bezeichnung ?? "").trim();
  if (!text) return undefined;
  const inKlammern = text.match(/\((\d{4})\)\s*$/);
  if (inKlammern && byBufa[inKlammern[1]]) return inKlammern[1];
  if (mehrdeutig.has(text)) return undefined;
  return list.find((f) => f.name === text)?.bufa;
}

/** BUFA (4-stellig) → Finanzamt, oder undefined. */
export function finanzamtByBufa(bufa: string): Finanzamt | undefined {
  const fa = byBufa[bufa];
  return fa ? { bufa, name: fa.name, ort: fa.ort, plz: fa.plz } : undefined;
}

/**
 * Suche über Ort, Name, BuFa und PLZ (diakritik-insensitiv). Leere Query →
 * ganze Liste.
 *
 * Sortiert wird nach Nähe zur Eingabe, nicht nach BuFa: Wer „münchen" tippt,
 * will nicht die Ämter sehen, die München nur im Namen führen, sondern die in
 * München. Vorher kam die Reihenfolge aus dem Generator (nach BuFa), weshalb
 * beim bloßen Öffnen des Feldes vierzig saarländische Ämter erschienen.
 */
export function searchFinanzaemter(query: string): Finanzamt[] {
  const q = norm((query ?? "").trim());
  if (!q) return list;
  const treffer = list.filter(
    (f) =>
      norm(f.ort).includes(q) ||
      norm(f.name).includes(q) ||
      f.bufa.startsWith(q) ||
      f.plz.startsWith(q),
  );
  const rang = (f: Finanzamt): number => {
    if (norm(f.ort) === q) return 0;
    if (norm(f.name) === q) return 1;
    if (f.bufa === q || f.plz === q) return 2;
    if (norm(f.ort).startsWith(q)) return 3;
    if (norm(f.name).startsWith(q)) return 4;
    return 5;
  };
  return treffer.sort((a, b) => rang(a) - rang(b) || a.bufa.localeCompare(b.bufa));
}
